import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, Image, FileText, Smartphone, Music, Play, Sparkles } from 'lucide-react';
import ContentTypeCard from './ContentTypeCard';
import ContentCreationModal from './ContentCreationModal';

// Platform mapping from content type platform to modal platform key
const PLATFORM_MAP = {
    'Instagram': 'instagram',
    'TikTok': 'tiktok',
    'LinkedIn': 'linkedin',
    'X / Twitter': 'twitter',
    'Twitter': 'twitter',
    'YouTube': 'youtube', // Fixed: YouTube should map to youtube for Shorts preview
    'Facebook': 'facebook',
    'Multi-platform': 'instagram', // Default
    'Podcast Apps': 'linkedin', // Default to professional style
    'Website': 'linkedin',
    'Email': 'linkedin',
    'Media': 'linkedin'
};

// Custom Preview Component for Coming Soon
const InstagramComingSoonPreview = () => (
    <div className="w-full h-full relative overflow-hidden bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 font-sans">
        <div className="absolute inset-0 bg-white/10" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '20px 20px' }} />

        {/* Content Container */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
            <motion.div
                initial={{ rotate: -5, scale: 0.9 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="w-full aspect-square max-w-[140px] bg-white/10 backdrop-blur-md border border-white/30 rounded-2xl flex flex-col items-center justify-center p-4 shadow-xl"
            >
                <div className="mb-3 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-lg">
                    <Sparkles size={20} className="text-purple-600" fill="currentColor" />
                </div>
                <h3 className="text-2xl font-black italic tracking-tighter text-center leading-[0.85] text-white drop-shadow-sm mb-2">
                    COMING<br />SOON
                </h3>
                <div className="px-3 py-1 bg-white/90 rounded-full shadow-sm">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-purple-700">New Episode</span>
                </div>
            </motion.div>
        </div>

        {/* Mock Instagram UI Elements */}
        <div className="absolute top-3 right-3 flex gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-white/60" />
            <div className="w-1.5 h-1.5 rounded-full bg-white/60" />
        </div>

        {/* Bottom Elements */}
        <div className="absolute bottom-3 left-0 right-0 flex justify-center opacity-80">
            <div className="flex items-center gap-1.5 bg-black/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10">
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                <span className="text-[8px] font-bold tracking-wide text-white uppercase">PodGuestly</span>
            </div>
        </div>
    </div>
);

/**
 * ContentTypeGrid - Grid layout for content type cards
 * 
 * Design matches the Pixar studio aesthetic:
 * - Responsive grid with consistent gaps
 * - Category-based content filtering
 * - Animated presence for category changes
 * - Integrated ContentCreationModal for content generation
 */
const ContentTypeGrid = ({ activeCategory, episode, planContent }) => {
    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedContentType, setSelectedContentType] = useState(null);
    const [selectedPlatform, setSelectedPlatform] = useState('instagram');
    // Content type definitions by category
    const contentTypes = {
        video: [
            {
                id: 'reels',
                name: 'Instagram Reels',
                platform: 'Instagram',
                aspectRatio: '9:16',
                icon: Video,
                iconColor: 'text-pink-600',
                iconBg: 'bg-pink-100',
                duration: '30-60s'
            },
            {
                id: 'tiktok',
                name: 'TikTok Video',
                platform: 'TikTok',
                aspectRatio: '9:16',
                icon: Video,
                iconColor: 'text-black',
                iconBg: 'bg-slate-900',
                duration: '15-60s'
            },
            {
                id: 'shorts',
                name: 'YouTube Shorts',
                platform: 'YouTube',
                aspectRatio: '9:16',
                icon: Video,
                iconColor: 'text-red-600',
                iconBg: 'bg-red-100',
                duration: 'Up to 60s'
            },
            {
                id: 'audiogram',
                name: 'Audiogram',
                platform: 'Multi-platform',
                aspectRatio: '1:1',
                icon: Music,
                iconColor: 'text-purple-600',
                iconBg: 'bg-purple-100',
                duration: '30-60s'
            },
            {
                id: 'highlight-clip',
                name: 'Highlight Clip',
                platform: 'YouTube',
                aspectRatio: '16:9',
                icon: Play,
                iconColor: 'text-red-600',
                iconBg: 'bg-red-100',
                duration: '3-10 min'
            },
            {
                id: 'stories',
                name: 'Instagram Stories',
                platform: 'Instagram',
                aspectRatio: '9:16',
                icon: Video,
                iconColor: 'text-pink-600',
                iconBg: 'bg-pink-100',
                duration: '15s'
            },
            {
                id: 'teaser',
                name: 'Episode Teaser',
                platform: 'Multi-platform',
                aspectRatio: '9:16',
                icon: Sparkles,
                iconColor: 'text-indigo-600',
                iconBg: 'bg-indigo-100',
                duration: '15-30s'
            },
            {
                id: 'bts',
                name: 'Behind the Scenes',
                platform: 'Multi-platform',
                aspectRatio: '9:16',
                icon: Video,
                iconColor: 'text-amber-600',
                iconBg: 'bg-amber-100',
                duration: '30-60s'
            }
        ],
        graphics: [
            {
                id: 'carousel',
                name: 'Instagram Carousel',
                platform: 'Instagram',
                aspectRatio: '1:1',
                icon: Image,
                iconColor: 'text-pink-600',
                iconBg: 'bg-pink-100'
            },
            {
                id: 'coming-soon',
                name: 'Coming Soon Post',
                platform: 'Instagram',
                aspectRatio: '1:1',
                icon: Sparkles,
                iconColor: 'text-indigo-600',
                iconBg: 'bg-indigo-100',
                PreviewComponent: InstagramComingSoonPreview
            },
            {
                id: 'thumbnail',
                name: 'YouTube Thumbnail',
                platform: 'YouTube',
                aspectRatio: '16:9',
                icon: Image,
                iconColor: 'text-red-600',
                iconBg: 'bg-red-100'
            },
            {
                id: 'quote-card',
                name: 'Quote Card',
                platform: 'Multi-platform',
                aspectRatio: '1:1',
                icon: FileText,
                iconColor: 'text-indigo-600',
                iconBg: 'bg-indigo-100'
            },
            {
                id: 'guest-spotlight',
                name: 'Guest Spotlight',
                platform: 'Multi-platform',
                aspectRatio: '1:1',
                icon: Image,
                iconColor: 'text-purple-600',
                iconBg: 'bg-purple-100'
            },
            {
                id: 'episode-cover',
                name: 'Episode Cover Art',
                platform: 'Podcast Apps',
                aspectRatio: '1:1',
                icon: Image,
                iconColor: 'text-green-600',
                iconBg: 'bg-green-100'
            }
        ],
        text: [
            {
                id: 'show-notes',
                name: 'Show Notes',
                platform: 'Podcast Apps',
                aspectRatio: '1:1',
                icon: FileText,
                iconColor: 'text-green-600',
                iconBg: 'bg-green-100'
            },
            {
                id: 'blog-post',
                name: 'Blog Post',
                platform: 'Website',
                aspectRatio: '1:1',
                icon: FileText,
                iconColor: 'text-blue-600',
                iconBg: 'bg-blue-100'
            },
            {
                id: 'email-newsletter',
                name: 'Email Newsletter',
                platform: 'Email',
                aspectRatio: '1:1',
                icon: FileText,
                iconColor: 'text-amber-600',
                iconBg: 'bg-amber-100'
            },
            {
                id: 'transcript-highlights',
                name: 'Transcript Highlights',
                platform: 'Multi-platform',
                aspectRatio: '1:1',
                icon: FileText,
                iconColor: 'text-slate-600',
                iconBg: 'bg-slate-100'
            }
        ],
        social: [
            {
                id: 'twitter-thread',
                name: 'Twitter Thread',
                platform: 'X / Twitter',
                aspectRatio: '1:1',
                icon: Smartphone,
                iconColor: 'text-slate-900',
                iconBg: 'bg-slate-100'
            },
            {
                id: 'linkedin-post',
                name: 'LinkedIn Post',
                platform: 'LinkedIn',
                aspectRatio: '1:1',
                icon: Smartphone,
                iconColor: 'text-blue-700',
                iconBg: 'bg-blue-100'
            },
            {
                id: 'facebook-post',
                name: 'Facebook Post',
                platform: 'Facebook',
                aspectRatio: '1:1',
                icon: Smartphone,
                iconColor: 'text-blue-600',
                iconBg: 'bg-blue-100'
            }
        ]
    };

    // Demo statuses for visual variety
    const demoStatuses = {
        video: ['empty', 'generating', 'generated', 'empty', 'empty', 'empty', 'empty', 'empty'],
        graphics: ['empty', 'generated', 'empty', 'generated', 'empty', 'empty'],
        text: ['generated', 'empty', 'empty', 'empty', 'empty'],
        social: ['empty', 'empty', 'generated', 'empty']
    };

    const currentContent = contentTypes[activeCategory] || [];
    const currentStatuses = demoStatuses[activeCategory] || [];

    // Handle opening the content creation modal
    const handleOpenModal = useCallback((contentType) => {
        setSelectedContentType(contentType);
        // Map the platform from content type to modal platform
        const modalPlatform = PLATFORM_MAP[contentType.platform] || 'instagram';
        setSelectedPlatform(modalPlatform);
        setIsModalOpen(true);
    }, []);

    // Handle closing the modal
    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedContentType(null);
    }, []);

    // Handle content generation from modal
    // Note: We don't close the modal here - let the user review the generated content
    // and close manually when done
    const handleGenerateContent = useCallback((content) => {
        // Handle both string content and object content (like posters)
        const contentPreview = typeof content === 'string'
            ? content?.substring(0, 100)
            : content?.posterUrl || JSON.stringify(content).substring(0, 100);
        console.log(`Content generated for ${selectedContentType?.name}:`, contentPreview);
        // Content is already set in the modal's state, no need to close
        // The user can review and make adjustments before closing manually
    }, [selectedContentType]);

    // Handle placeholder actions
    const handleAction = (action, contentType) => {
        if (action === 'generate' || action === 'preview') {
            handleOpenModal(contentType);
        } else {
            console.log(`${action} clicked for ${contentType.id}`);
        }
    };

    return (
        <>
            <div className="px-6 py-4">
                {/* Section Header */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                        {activeCategory === 'video' && 'Video Content'}
                        {activeCategory === 'graphics' && 'Static Graphics'}
                        {activeCategory === 'text' && 'Text Documents'}
                        {activeCategory === 'social' && 'Social Posts'}
                    </h2>
                    <span className="text-xs text-slate-500 font-medium">
                        {currentContent.length} types available
                    </span>
                </div>

                {/* Content Grid */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeCategory}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.2 }}
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
                    >
                        {currentContent.map((contentType, index) => (
                            <ContentTypeCard
                                key={contentType.id}
                                contentType={contentType}
                                status={currentStatuses[index] || 'empty'}
                                onGenerate={() => handleAction('generate', contentType)}
                                onEdit={() => handleAction('edit', contentType)}
                                onPublish={() => handleAction('publish', contentType)}
                                onPreview={() => handleAction('preview', contentType)}
                            />
                        ))}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Content Creation Modal */}
            <ContentCreationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                initialPlatform={selectedPlatform}
                contentType={selectedContentType}
                episode={episode}
                planContent={planContent}
                onGenerate={handleGenerateContent}
            />
        </>
    );
};

export default ContentTypeGrid;